import { Suspense, useState } from "react";
import { Grid, RadioGroup, styled, Typography } from "@mui/material";
import { Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { FilterItem, Poi, PoiTimeseriesType } from "src/types";
import { RadioChip } from "./RadioChip";
import { TimeseriesGraph } from "./TimeSeriesGraph";
import { InfoIcon } from "src/components/icons/InfoIcon";

const mapTypes: FilterItem<PoiTimeseriesType>[] = [
  { id: PoiTimeseriesType.HOURLY, name: PoiTimeseriesType.HOURLY },
  { id: PoiTimeseriesType.DAILY, name: PoiTimeseriesType.DAILY },
];

enum SelectionKey {
  substance = "substance",
  mapType = "mapType",
}

interface Selection {
  [SelectionKey.substance]: number | string;
  [SelectionKey.mapType]: PoiTimeseriesType;
}

interface Props {
  scenarios: FilterItem[];
  substances: FilterItem[];
  poi: Poi;
  ospmEnabled: boolean;
}

export function PoiResult(props: Props) {
  const { substances, ospmEnabled } = props;
  const { t } = useTranslation();
  const [selection, setSelection] = useState<Selection>({
    [SelectionKey.substance]: substances[0].id,
    [SelectionKey.mapType]: mapTypes[1].id,
  });

  /**
   * Functions
   */
  function handleChangeSelection(selectionKey: SelectionKey, id: string | number) {
    setSelection({ ...selection, [selectionKey]: id });
  }

  // render the component
  return render();

  function render() {
    return (
      <Styles>
        <Grid container>
          <Grid item xs={8} flexDirection="column">
            <RadioGroup value={selection.substance} className="radio-group">
              {substances.map((substance) => (
                <RadioChip
                  key={substance.id}
                  value={substance.id}
                  label={<span dangerouslySetInnerHTML={{ __html: substance.name }} />}
                  onClick={() => handleChangeSelection(SelectionKey.substance, substance.id)}
                />
              ))}
            </RadioGroup>
          </Grid>
          <Grid item xs={4} container justifyContent="flex-end" alignContent="flex-end">
            <RadioGroup value={selection.mapType} className="radio-group">
              {mapTypes.map((mapType) => (
                <RadioChip
                  key={mapType.id}
                  value={mapType.id}
                  label={t(`label.poiTimeseriesType.${mapType.name}`)}
                  onClick={() => handleChangeSelection(SelectionKey.mapType, mapType.id)}
                />
              ))}
            </RadioGroup>
          </Grid>
          {ospmEnabled && (
            <Grid item xs={12} className="extra-info-grid">
              <InfoIcon color="info" fontSize="small" />
              <Typography variant="small">{t("label.poiTimeseriesInfo")}</Typography>
            </Grid>
          )}
          <Grid className="chart">
            <Suspense fallback={<Skeleton variant="rectangular" width="100%" height="100%" animation="pulse" />}>
              <TimeseriesGraph
                poiId={props.poi.id}
                scenarios={props.scenarios}
                substanceId={selection[SelectionKey.substance]}
                type={selection[SelectionKey.mapType]}
              />
            </Suspense>
          </Grid>
        </Grid>
      </Styles>
    );
  }
}

const Styles = styled("div")`
  width: 100%;
  min-width: 800px;

  .radio-group {
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(1)};
  }

  .extra-info-grid {
    padding: ${({ theme }) => theme.spacing(2, 0, 1, 0)};
    display: inline-flex;
    gap: ${({ theme }) => theme.spacing(0.5)};

    .MuiTypography-root {
      margin: 0;
    }
  }

  .chart {
    height: 600px;
    width: 100%;
  }
`;
